// src/client/game/ui/LeaderboardOverlay.ts

import Phaser from "phaser";
import type { RunStats } from "../core/model";

type LeaderboardEntry = {
  userName: string;
  score: number;
  rank: number;
  timeMs?: number;
  laserLevel?: number;
};

type LeaderboardResponse = {
  entries: LeaderboardEntry[];
  self: LeaderboardEntry | null;
};

function formatTime(ms?: number): string {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return "—";
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatLaser(level?: number): string {
  if (typeof level !== "number" || !Number.isFinite(level) || level <= 0)
    return "—";
  return `Lv${Math.floor(level)}`;
}

export class LeaderboardOverlay {
  private objects: Phaser.GameObjects.GameObject[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly getRunStats: () => RunStats,
    private readonly onPlayAgain: () => void
  ) {}

  clear(): void {
    for (const obj of this.objects) obj.destroy();
    this.objects = [];
  }

  /** Called from SnakeGameScene when showing leaderboard interactively */
  submitAndShow(): void {
    const run = this.getRunStats();
    this.submitScore(run, true);
  }

  /** Called from GameOverOverlay to silently update rank info */
  submitScoreOnly(score: number): void {
    const run = this.getRunStats();
    run.score = score;
    this.submitScore(run, false);
  }

  /** Handles sending a score to the backend and optionally showing overlay */
  private submitScore(run: RunStats, showAfter: boolean): void {
    fetch("/api/submit-score", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(run),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: LeaderboardResponse) => {
        if (showAfter) this.render(data, run);
      })
      .catch((err) => {
        console.error("Leaderboard error", err);
      });
  }

  private render(data: LeaderboardResponse, run: RunStats): void {
    this.clear();

    const cam = this.scene.cameras.main;
    const width = cam.width;
    const height = cam.height;
    const isNarrow = width < 420;

    const panelWidth = Math.min(420, width * 0.85);
    const panelHeight = Math.min(isNarrow ? 340 : 380, height * 0.8);

    const centerX = width / 2;
    const centerY = height / 2;

    const titleFontSize = isNarrow ? "16px" : "18px";
    const bodyFontSize = isNarrow ? "13px" : "14px";
    const scoreFontSize = isNarrow ? "18px" : "20px";
    const lineGap = isNarrow ? 18 : 20;

    const currentScore = run.score;
    const bestScore = data.self ? data.self.score : currentScore;

    // Panel
    const bg = this.scene.add.rectangle(
      centerX,
      centerY,
      panelWidth,
      panelHeight,
      0x000000,
      0.85
    );
    bg.setStrokeStyle(2, 0xffffff);
    bg.setDepth(1000);
    this.objects.push(bg);

    let y = centerY - panelHeight / 2 + 16;

    const title = this.scene.add.text(centerX, y, "Leaderboard", {
      fontFamily: "monospace",
      fontSize: titleFontSize,
      color: "#ffffff",
    });
    title.setOrigin(0.5, 0);
    title.setDepth(1001);
    this.objects.push(title);

    y += lineGap + 6;

    const maxLines = isNarrow ? 5 : 7;
    const entries = data.entries.slice(0, maxLines);

    entries.forEach((entry) => {
      const line = this.scene.add.text(
        centerX,
        y,
        `${entry.rank}. ${entry.userName ?? "Player"} – ${
          entry.score
        }  (${formatLaser(entry.laserLevel)}, ${formatTime(entry.timeMs)})`,
        {
          fontFamily: "monospace",
          fontSize: bodyFontSize,
          color: "#dddddd",
        }
      );
      line.setOrigin(0.5, 0);
      line.setDepth(1001);
      this.objects.push(line);
      y += lineGap;
    });

    // Always show your line (derived rank if possible)
    if (data.self) {
      const self = data.self;

      let derivedRank: number | null = null;
      const exact = entries.find(
        (e) =>
          (e.userName ?? "") === (self.userName ?? "") &&
          e.score === self.score
      );
      if (exact) derivedRank = exact.rank;
      else {
        const byName = entries.find(
          (e) => (e.userName ?? "") === (self.userName ?? "")
        );
        if (byName) derivedRank = byName.rank;
      }

      const rankToShow = derivedRank ?? self.rank;

      y += 6;
      const meLine = this.scene.add.text(
        centerX,
        y,
        `You: #${rankToShow} – ${self.score}  (${formatLaser(
          self.laserLevel
        )}, ${formatTime(self.timeMs)})`,
        {
          fontFamily: "monospace",
          fontSize: bodyFontSize,
          color: "#ffff00",
        }
      );
      meLine.setOrigin(0.5, 0);
      meLine.setDepth(1001);
      this.objects.push(meLine);
      y += lineGap;
    }

    // Your score / best score
    y += 10;
    const yourScoreText = this.scene.add.text(
      centerX,
      y,
      `Your score: ${currentScore}  (${formatLaser(
        run.laserLevel
      )}, ${formatTime(run.timeMs)})`,
      {
        fontFamily: "monospace",
        fontSize: scoreFontSize,
        color: "#ffff66",
      }
    );
    yourScoreText.setOrigin(0.5, 0);
    yourScoreText.setDepth(1001);
    this.objects.push(yourScoreText);

    y += lineGap;
    const bestScoreText = this.scene.add.text(
      centerX,
      y,
      `Best score: ${bestScore}`,
      {
        fontFamily: "monospace",
        fontSize: scoreFontSize,
        color: "#ffd347",
      }
    );
    bestScoreText.setOrigin(0.5, 0);
    bestScoreText.setDepth(1001);
    this.objects.push(bestScoreText);

    // --- Play again button OUTSIDE the panel ---
    const buttonWidth = Math.min(220, width * 0.6);
    const buttonHeight = 40;

    const panelBottom = centerY + panelHeight / 2;
    const buttonY = Math.min(
      height - buttonHeight / 2 - 8,
      panelBottom + 15 + buttonHeight / 2
    );

    const button = this.scene.add.rectangle(
      centerX,
      buttonY,
      buttonWidth,
      buttonHeight,
      0xffd347
    );
    button.setOrigin(0.5);
    button.setStrokeStyle(2, 0x000000);
    button.setDepth(1001);
    button.setInteractive({ useHandCursor: true });
    this.objects.push(button);

    const buttonLabel = this.scene.add.text(centerX, buttonY, "Play again", {
      fontFamily: "monospace",
      fontSize: isNarrow ? "18px" : "20px",
      color: "#000000",
    });
    buttonLabel.setOrigin(0.5);
    buttonLabel.setDepth(1002);
    this.objects.push(buttonLabel);

    button.on("pointerup", () => {
      this.onPlayAgain();
    });
  }
}
